import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../../Layout/DashboardLayout";
import {
  Icon,
  Dropdown,
  Textarea,
  useAuth,
  employeeID,
  companyID,
  sanitizeLabName,
  normalizeSpaces,
  CancelButton,
} from "../../../Common/UIComponents";
import {
  saveTestType,
  fetchBasicStandards,
} from "../../../API_Service/Settings_Slice";

const NAME_MAX_LENGTH = 50;
const DESC_MAX_LENGTH = 600;

const CharCounter = ({ value, max }) => {
  const len = (value ?? "").length;
  if (len === 0) return null;
  const atLimit = len === max;
  return (
    <div className={`flex items-center gap-1 mt-1 text-xs ${atLimit ? "text-red-500 font-semibold" : "text-[#3e494a]/60"}`}>
      <span>{len}/{max}</span>
      {atLimit && <span className="text-red-500">— limit reached</span>}
    </div>
  );
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const toOptions = (items) =>
  items.map((item) => ({
    value: String(item.value ?? item.Value ?? ""),
    label: String(item.label ?? item.Label ?? ""),
  }));

// ─────────────────────────────────────────────
// EMPTY FORM — used to reset after create
// ─────────────────────────────────────────────

const EMPTY_FORM = {
  name: "",
  basicStandardId: "",
  classification: "",
  facilityIDs: [],
  description: "",
};

// ─────────────────────────────────────────────
// FIELD COMPONENTS
// ─────────────────────────────────────────────

const FieldLabel = ({ children, required }) => (
  <label className="block text-sm font-bold text-[#181c1d] ml-1 mb-2">
    {children}
    {required && <span className="text-[#ba1a1a] ml-1">*</span>}
  </label>
);

const TextInput = ({ placeholder, value, onChange, error, maxLength }) => (
  <div>
    <input
      type="text"
      value={value}
      onChange={(e) => { const v = e.target.value.replace(/^\s+/, ""); onChange({ ...e, target: { ...e.target, value: v } }); }}
      placeholder={placeholder}
      maxLength={maxLength}
      className={`w-full bg-[#e5e9e9] border-none rounded-xl px-4 py-2.5 text-[#181c1d]
                  placeholder:text-[#3e494a]/40 text-sm
                  focus:ring-2 focus:ring-[#08757c] focus:bg-white transition-all outline-none
                  ${error ? "ring-2 ring-[#ba1a1a]" : ""}`}
    />
    {error && <p className="text-xs text-[#ba1a1a] mt-1 ml-1">{error}</p>}
  </div>
);

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function TestTypeConfigCreate({ embedded = false, onCancel, onSuccess } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isEditMode = location.state?.mode === "edit";
  const editData = location.state?.data ?? {};

  // ── Dropdown options ──────────────────────────
  const [basicStandardOptions, setBasicStandardOptions] = useState([]);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [classificationOptions, setClassificationOptions] = useState([]);

  // ── UI state ──────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  // ── Form state ────────────────────────────────
  const [form, setForm] = useState({
    name: editData.name ?? "",
    basicStandardId: String(editData.basicStandardId ?? ""),
    classification: String(editData.classificationId ?? ""),
    facilityIDs: editData.facilityIDs
      ? editData.facilityIDs.split(",").map((f) => f.trim()).filter(Boolean)
      : [],
    description: editData.description ?? "",
  });

  // ── Fetch all 3 dropdowns on mount ───────────
  useEffect(() => {
    dispatch(fetchBasicStandards({ term: "BASICSTANDARD", companyID: companyID }))
      .then((res) => setBasicStandardOptions(toOptions(toArray(res))))
      .catch(() => {});

    dispatch(fetchBasicStandards({ term: "FACILITIES", companyID: companyID }))
      .then((res) => setFacilityOptions(toOptions(toArray(res))))
      .catch(() => {});

    dispatch(fetchBasicStandards({ term: "TESTCLASSIFICATION", companyID: companyID }))
      .then((res) => setClassificationOptions(toOptions(toArray(res))))
      .catch(() => {});
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ─────────────────────────────────
  const handleField = useCallback((field) => (e) => {
    const raw = e.target.value;
    const val = field === "name" ? sanitizeLabName(raw) : raw;
    setForm((prev) => ({ ...prev, [field]: val }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    if (field === "name") setSaveError(null);
    setSaveSuccess(false);
  }, []);

  const handleSelect = useCallback((field) => (val) => {
    setForm((prev) => ({ ...prev, [field]: val }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setSaveSuccess(false);
  }, []);

  // ── Validation ────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.name.trim())
      errs.name = "Test Type Name is required.";
    if (!form.basicStandardId)
      errs.basicStandardId = "Please select a Basic Standard.";
    if (!form.classification)
      errs.classification = "Please select a Test Classification.";
    if (!form.facilityIDs || form.facilityIDs.length === 0)
      errs.facilityIDs = "Please select at least one Facility.";
    return errs;
  };

  // ── Save ─────────────────────────────────────
  // Mirrors LocationConfigCreate exactly:
  //   CREATE → stay on page, reset form, show success banner, hide after 4s
  //   UPDATE → navigate back to list with editSuccess: true
  const handleSave = useCallback(() => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      TestType: {
        TestTypeID:           isEditMode ? (editData.id ?? 0) : 0,
        TestTypeName:         normalizeSpaces(form.name),
        BasicStandardID:      Number(form.basicStandardId),
        BasicStandardName:    basicStandardOptions.find(
                                (o) => String(o.value) === String(form.basicStandardId)
                              )?.label ?? "",
        StrFacilitiesIDs:     Array.isArray(form.facilityIDs)
                                ? form.facilityIDs.join(",")
                                : String(form.facilityIDs ?? ""),
        TestDescription:      normalizeSpaces(form.description),
        TestClassificationId: Number(form.classification),
        Flag:                 isEditMode ? 1 : 0,
      },
      CreatedBy: employeeID,
      CompanyID: companyID,
    };

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    dispatch(saveTestType(payload))
      .then((res) => {
        const result = res?.payload?.data ?? res?.payload;

        if (Number(result) === -1) {
          // Duplicate name
          setSaveError("A Test Type with this name already exists.");

        } else if (Number(result) > 0) {

          if (embedded) {
            // ── EMBEDDED: hand the new id back to the caller and let them close ──
            onSuccess?.(Number(result), normalizeSpaces(form.name));
          } else if (isEditMode) {
            // ── UPDATE: navigate back, list shows "Updated successfully" toast ──
            navigate("/application-config", {
              state: { tab: "testtype", refresh: true, editSuccess: true },
            });
          } else {
            // ── CREATE: stay on page, clear form, show inline success banner ──
            setSaveSuccess(true);
            setForm(EMPTY_FORM);
            setErrors({});
            setTimeout(() => setSaveSuccess(false), 4000);
          }

        } else {
          setSaveError("Failed to save. Please try again.");
        }
      })
      .catch((err) => setSaveError(err?.message ?? "Failed to save test type"))
      .finally(() => setIsSaving(false));
  }, [form, isEditMode, editData, basicStandardOptions, dispatch, navigate, companyID, employeeID]);

  const handleCancel = useCallback(
    () => (embedded ? onCancel?.() : navigate("/application-config", { state: { tab: "testtype" } })),
    [navigate, embedded, onCancel]
  );

  const saveLabel = isEditMode ? "Update Test Type" : "Save Test Type";

  const SaveButton = () => (
    <button
      onClick={handleSave}
      disabled={isSaving}
      className="px-8 py-3 rounded-xl bg-gradient-to-br from-[#08757c] to-[#005b60] text-white text-sm font-bold shadow-lg shadow-[#08757c]/20 hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isSaving ? (
        <><span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>Saving…</>
      ) : (
        <><span className="material-symbols-outlined text-[18px]">save</span>{saveLabel}</>
      )}
    </button>
  );

  const BackToListButton = () => (
    <button
      onClick={handleCancel}
      className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-slate-600 font-bold text-sm hover:bg-slate-100 transition-colors"
    >
      <span className="material-symbols-outlined text-lg">arrow_back</span>
      Back to List
    </button>
  );

  const Wrapper = embedded ? React.Fragment : DashboardLayout;

  return (
    <Wrapper>
      <div className="px-4 py-5 bg-[#f7fafa] min-h-screen text-[#181c1d]">
        <div>

          {/* ── Header ── */}
          <div className="flex items-start justify-between mb-5">
            <div>
              {!embedded && (
                <>
                  <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
                    <button onClick={handleCancel} className="hover:text-[#08757c] transition-colors">
                      App Configuration
                    </button>
                    <span className="material-symbols-outlined text-[14px]">chevron_right</span>
                    <button onClick={handleCancel} className="hover:text-[#08757c] transition-colors">
                      Test Type Management
                    </button>
                    <span className="material-symbols-outlined text-[14px]">chevron_right</span>
                    <span className="text-[#08757c] font-semibold">
                      {isEditMode ? "Edit Test Type" : "Add New Test Type"}
                    </span>
                  </nav>
                  <button
                    onClick={handleCancel}
                    className="group flex items-center gap-1.5 text-[#08757c] text-sm font-semibold mb-2 hover:opacity-75 transition-opacity"
                  >
                    <span className="material-symbols-outlined text-[18px] transition-transform group-hover:-translate-x-1">
                      arrow_back
                    </span>
                    Back to List
                  </button>
                </>
              )}
              <h2 className="text-3xl font-extrabold tracking-tight">
                {isEditMode ? "Edit Test Type" : "Add New Test Type"}
              </h2>
              <p className="text-[#3e494a] text-sm mt-1 max-w-xl">
Add and configure test types for your laboratory. Assign relevant facilities to ensure accurate test routing and reporting.              </p>
            </div>
            <div className={`flex gap-3 pt-4 ${embedded ? "mr-12" : ""}`}>
              {embedded ? <CancelButton onClick={handleCancel} /> : <BackToListButton />}
              <SaveButton />
            </div>
          </div>

          {/* ── Form Card ── */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#bdc9c9]/20">
            <div className="space-y-5">

              {/* Error Banner */}
              {saveError && (
                <div className="bg-red-50 text-[#ba1a1a] rounded-xl px-5 py-4 text-sm flex items-center gap-3">
                  <span className="material-symbols-outlined text-[20px]">error</span>
                  {saveError}
                </div>
              )}

              {/* Test Type Name */}
              <div>
                <FieldLabel required>Test Type Name</FieldLabel>
                <TextInput
                  placeholder="e.g. Molecular Spectrometry"
                  value={form.name}
                  onChange={handleField("name")}
                  error={errors.name}
                  maxLength={NAME_MAX_LENGTH}
                />
                <CharCounter value={form.name} max={NAME_MAX_LENGTH} />
              </div>

              {/* Basic Standard + Classification */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel required>Basic Standard</FieldLabel>
                  <Dropdown
                    options={basicStandardOptions}
                    value={form.basicStandardId}
                    onChange={handleSelect("basicStandardId")}
                    placeholder="Select Standard"
                    searchPlaceholder="Search standards…"
                    error={errors.basicStandardId}
                  />
                </div>
                <div>
                  <FieldLabel required>Test Classification</FieldLabel>
                  <Dropdown
                    options={classificationOptions}
                    value={form.classification}
                    onChange={handleSelect("classification")}
                    placeholder="Select Classification"
                    searchPlaceholder="Search classification…"
                    error={errors.classification}
                  />
                </div>
              </div>

              {/* Facilities */}
              <div>
                <FieldLabel required>Facilities</FieldLabel>
                <Dropdown
                  options={facilityOptions}
                  value={form.facilityIDs}
                  onChange={handleSelect("facilityIDs")}
                  placeholder="Select Facilities"
                  searchPlaceholder="Search facilities…"
                  multi
                  error={errors.facilityIDs}
                />
              </div>

              {/* Description */}
              <div>
                <FieldLabel>Test Type Description</FieldLabel>
                <Textarea
                  id="tt-description"
                  value={form.description}
                  onChange={handleField("description")}
                  placeholder="Provide detailed clinical specifications and testing parameters..."
                  rows={8}
                  maxLength={DESC_MAX_LENGTH}
                />
                <CharCounter value={form.description} max={DESC_MAX_LENGTH} />
              </div>

              {/* Info note */}
              <div className="flex items-center gap-3 p-4 bg-[#f1f4f4] rounded-xl border-l-4 border-[#08757c]">
                <span className="material-symbols-outlined text-[#08757c] text-[20px]">info</span>
                <p className="text-xs text-[#3e494a] font-medium">
                  Name, Basic Standard, Classification, and at least one Facility are mandatory.
                </p>
              </div>

              {/* ── Success Banner (create mode only) ──
                  Same as LocationConfigCreate: stays on page, clears form,
                  shows this banner, auto-hides after 4 seconds.
              */}
              {saveSuccess && (
                <div className="bg-[#bbf46e]/20 text-[#436900] rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
                  <span className="material-symbols-outlined text-[20px] text-[#436900]">check_circle</span>
                  <span className="font-semibold">
                    Test Type created successfully. You can add another one.
                  </span>
                </div>
              )}

            </div>
          </div>

          {/* Bottom action bar */}
          <div className="flex justify-end gap-3 mt-4">
            {embedded ? <CancelButton onClick={handleCancel} /> : <BackToListButton />}
            <SaveButton />
          </div>

        </div>
      </div>
    </Wrapper>
  );
}
